import React from "react";
import { useFormik } from "formik";
import * as yup from "yup";
// MUi
import styled from "@emotion/styled";
import MKButton from "components/MKButton";
import MKTypography from "components/MKTypography";
import { Box, Grid, Stack, TextField } from "@mui/material";
import MKBox from "components/MKBox";
// Components & Styles
import { StyledFooterLink } from "index.styles";
// Theme
import theme from "assets/theme";
// Hooks
import useGhlNewsLetterWebhook from "../../hooks/useGhlNewsLetterWebhook";
import useVerifyRecaptcha from "utils/hooks/useVerifyRecaptcha";
import useRecaptchaEnterprise from "components/recaptchaCheck/useRecaptchaEnterprise";
// Components
import RecaptchaBadge from "components/recaptchaCheck/recaptchaBadge";

const StyledFooterInputContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  position: "relative",
  marginTop: "1rem",
}));

const validationSchema = yup.object({
  email: yup
    .string()
    .trim()
    .email("Please enter a valid email address")
    .matches(
      /^[^\s@]+@[^\s@]+\.(com|org|net|edu|gov|mil|info|biz|co|me|io|tech|online|us|uk|ca|au|ac|health|med|doctor|academy|institute|college|law|consulting|clinic|usp\.br)$/,
      "Email must end with valid domain suffixes."
    )
    .required("Email is required"),
});

const initialValues = {
  email: "",
};
const FooterForm = () => {
  const { loading, error, status, sendToWebhook } = useGhlNewsLetterWebhook();
  const {
    verifyRecaptcha,
    error: verifyRecaptchaError,
    loading: verifyRecaptchaLoading,
    success: verifyRecaptchaSuccess,
  } = useVerifyRecaptcha();
  const {
    isReady,
    isExecuting,
    error: recaptchaError,
    executeRecaptcha,
  } = useRecaptchaEnterprise();

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      // Check if reCAPTCHA is ready
      if (!isReady) {
        alert("reCAPTCHA is still loading. Please try again.");
        return;
      }

      try {
        // Execute reCAPTCHA to get token
        const recaptchaToken = await executeRecaptcha("alma_newsletter_signup");
        if (!recaptchaToken) {
          alert(recaptchaError || "reCAPTCHA verification failed. Please try again.");
          return;
        }

        // Verify token with backend
        const isRecaptchaValid = await verifyRecaptcha(recaptchaToken, "alma_newsletter_signup");
        if (isRecaptchaValid) {
          await sendToWebhook(values);
          resetForm();
        } else {
          alert(verifyRecaptchaError || "Verification failed. Please try again.");
        }
      } catch (error) {
        console.error("reCAPTCHA verification failed:", error);
        alert("An error occurred. Please try again.");
      }
    },
  });

  return (
    <Grid
      autoComplete="off"
      item
      xs={12}
      xl={5}
      sx={{ minHeight: "178px" }}
      component={"form"}
      onSubmit={formik.handleSubmit}
      noValidate
      role="form"
      aria-label="Newsletter signup form"
    >
      <MKTypography sx={{ color: theme.palette.white.main }} component="h2" variant="button" fontWeight="bold" textTransform="capitalize" mb={1}>
        Let's stay in touch!
      </MKTypography>
      <MKTypography sx={{ color: theme.palette.white.main }} component="p" variant={"button"} textTransform="capitalize" gutterBottom>
        Learn more about the latest ketamine therapy news <br />
        and special offers for our programs.
      </MKTypography>
      <StyledFooterInputContainer>
        <Box sx={{ width: "100%" }}>
          <MKTypography htmlFor="footer-email-input" component="label" variant="body2" sx={{ color: theme.palette.white.main }}>
            Email address *
          </MKTypography>
          <TextField
            id="footer-email-input"
            sx={{
              minWidth: "100%",
              marginBottom: formik.values.email ? "1rem" : "0",
            }}
            name="email"
            type="email"
            placeholder="Enter Email Here..."
            value={formik.values.email}
            onChange={(e) => {
              formik.handleChange(e);
            }}
            autoComplete="email"
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
            helpertextstyle={{ color: "red !important" }}
            inputProps={{
              "aria-label": "Email address for newsletter signup",
              "aria-describedby": formik.touched.email && formik.errors.email ? "email-error" : undefined,
            }}
          />
        </Box>
        <MKButton
          disabled={!isReady || isExecuting || loading || verifyRecaptchaLoading || verifyRecaptchaSuccess}
          type="submit"
          size="small"
          color="secondary"
          variant={"contained"}
          sx={{
            display: "block",
            borderRadius: "6px",
            position: "absolute",
            top: "29px",
            // top: "51%",
            // transform: "translateY(-50%)",
            right: 0,
            height: "45px",
          }}
          aria-label={
            isExecuting || verifyRecaptchaLoading
              ? "Verifying reCAPTCHA"
              : loading
              ? "Submitting newsletter signup"
              : error || verifyRecaptchaError
              ? "Newsletter signup failed, try again"
              : status || verifyRecaptchaSuccess
              ? "Newsletter signup successful"
              : "Submit newsletter signup"
          }
        >
          {isExecuting || verifyRecaptchaLoading
            ? "Verifying..."
            : loading
            ? "Sending..."
            : error || verifyRecaptchaError
            ? "Error..."
            : status || verifyRecaptchaSuccess
            ? "Success!"
            : "Sign up"}
        </MKButton>
      </StyledFooterInputContainer>

      {/* Screen reader status updates */}
      <div
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: "absolute",
          left: "-10000px",
          width: "1px",
          height: "1px",
          overflow: "hidden",
        }}
      >
        {isExecuting || verifyRecaptchaLoading
          ? "Verifying reCAPTCHA"
          : loading
          ? "Submitting newsletter signup form"
          : error || verifyRecaptchaError
          ? "Newsletter signup failed. Please try again."
          : status || verifyRecaptchaSuccess
          ? "Newsletter signup successful! Thank you for subscribing."
          : ""}
      </div>
      {formik.values.email && (
        <MKBox>
          <MKTypography
            sx={{ color: theme.palette.white.main, fontSize: "10px", mt: 1, display: "inline-block", zIndex: 500 }}
            component="p"
            textTransform="capitalize"
            gutterBottom
          >
            By clicking "Sign Up", you agree to receive automated reminders and promotional messages from Almadelic Management Services, LLC. You also
            agree to our <StyledFooterLink to="/terms">Terms Of Service</StyledFooterLink>
            &nbsp; and &nbsp;<StyledFooterLink to="/privacy-policy">Privacy Policy</StyledFooterLink>. This agreement isn't a condition of any
            purchase. Msg & Data rates may apply. Reply STOP to end or HELP for help.
          </MKTypography>
        </MKBox>
      )}

      {/* reCAPTCHA Enterprise Badge */}
      <RecaptchaBadge variant="dark" />
    </Grid>
  );
};

export default FooterForm;
